import React, { useMemo, useState } from 'react';
import { Spacer, Icon } from 'components/atoms';
import styled, { css, withTheme } from 'styled-components/macro';
import PropTypes from 'prop-types';
import { modifiersType, themeType } from 'types';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { lighten } from 'polished'

const AVATAR_MODIFIERS = {
  xl9: ({ theme }) => css`
    min-height: ${theme.sizes.xl9};
    min-width: ${theme.sizes.xl9};
    font-size: ${theme.sizes.xl5};
  `,
  xl: ({ theme }) => css`
    width: ${theme.sizes.xl};
    height: ${theme.sizes.xl};
    min-height: ${theme.sizes.xl};
    min-width: ${theme.sizes.xl};
  `,
  bgLighterS: ({ theme }) => css`
    background-color: ${theme.colors.bgLighterS};
  `,
};

const IMAGE_MODIFIERS = {
  xl9: ({ theme }) => css`
    height: ${theme.sizes.xl9};
    width: ${theme.sizes.xl9};
`,
  xl: ({ theme }) => css`
    width: ${theme.sizes.xl};
    height: ${theme.sizes.xl};
  `,
}

const AvatarBase = ({
  name,
  className,
  showName,
  onClick,
  modifiers,
  activeColor,
  image,
  shouldInteract,
  theme,
  loading,
  iconHeight,
}) => {
  const hasImage = useMemo(() => image !== null, [image])

  // icon
  const [isHover, setHover] = useState(false);
  const computedIconColor = useMemo(() => {
    const isInverse = modifiers.includes('inverse')
    const isLight = modifiers.includes('bgLighterS')
    if (isHover) {
      const color = activeColor || theme.colors.primary
      return isInverse ? lighten(0.4, color) : color
    }
    if (isInverse) return theme.colors.baseInverse
    return isLight ? theme.colors.baseLighterS : theme.colors.base
  }, [modifiers, isHover, theme.colors, activeColor])

  if (loading) {
    return (
      <Avatar modifiers={modifiers} hasImage={false} />
    )
  }

  return (
    <Wrapper
      showName={showName}
      onClick={onClick}
      hasImage={hasImage}
      shouldInteract={shouldInteract}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={className}
    >
      <Avatar modifiers={modifiers} hasImage={hasImage}>
        {image ? (
          <Avatar.Image src={image} modifiers={modifiers} alt={`Foto de ${name}`} />
        ) : <Avatar.Icon icon="fal fa-user" height={iconHeight} color={computedIconColor} />
        }
      </Avatar>
      {showName && (
        <Spacer left="xs">
          <p>{name}</p>
        </Spacer>
      )}
    </Wrapper>
  );
};

const Avatar = styled.div`
  height: ${props => props.theme.sizes.xl4};
  width: ${props => props.theme.sizes.xl4};
  min-height: ${props => props.theme.sizes.xl4};
  min-width: ${props => props.theme.sizes.xl4};
  color: ${props => props.theme.colors.baseInverse};
  font-size: ${props => props.theme.sizes.s};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  ${applyStyleModifiers(AVATAR_MODIFIERS)};
`;

Avatar.Image = styled.img`
  height: ${props => props.theme.sizes.xl4};
  width: ${props => props.theme.sizes.xl4};
  object-fit: cover;
  ${applyStyleModifiers(IMAGE_MODIFIERS)};
`

Avatar.Icon = styled(Icon)``

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${props => (props.shouldInteract ? 'auto' : 'none')};
  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: ${props => (props.showName ? '136px' : '40px')};
  }

  &:hover {
    cursor: pointer;
    color: ${props => props.theme.colors.primary};
  }
`;

AvatarBase.propTypes = {
  name: PropTypes.string,
  showName: PropTypes.bool,
  onClick: PropTypes.func,
  modifiers: modifiersType,
  image: PropTypes.string,
  shouldInteract: PropTypes.bool,
  loading: PropTypes.bool,
  theme: themeType.isRequired,
  className: PropTypes.string,
  activeColor: PropTypes.string,
  iconHeight: PropTypes.number,
};

AvatarBase.defaultProps = {
  name: null,
  showName: false,
  onClick: () => null,
  modifiers: '',
  image: null,
  shouldInteract: true,
  loading: false,
  className: '',
  activeColor: null,
  iconHeight: 24,
};

export default withTheme(AvatarBase);
